import React, { useState, useRef, useEffect } from 'react';
import MobileBottomSheet from './MobileBottomSheet';
import { TEXT_3D_PRESETS } from '../../core/TextEffects3D';
import { getSuggestions, detectContext, CATEGORY_LABELS, CATEGORY_ICONS } from '../../data/textSuggestions';

const TEXT_PRESETS = [
  { id: 'h1', label: 'Überschrift', preview: 'Überschrift', fontSize: 72, fontWeight: 'bold', desc: '72px, Fett' },
  { id: 'h2', label: '2. Überschrift', preview: 'Zweite Überschrift', fontSize: 48, fontWeight: 'bold', desc: '48px, Fett' },
  { id: 'h3', label: '3. Überschrift', preview: 'Dritte Überschrift', fontSize: 36, fontWeight: '600', desc: '36px, Halbfett' },
  { id: 'body', label: 'Normaler Text', preview: 'Normaler Fließtext', fontSize: 24, fontWeight: 'normal', desc: '24px, Normal' },
];

const EFFECT_CATEGORIES = [...new Set(TEXT_3D_PRESETS.map(p => p.category))];

export default function MobileTextSheet({ isOpen, onClose, canvasManager }) {
  const [customText, setCustomText] = useState('');
  const [tab, setTab] = useState('standard');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  const scale = canvasManager?.getObjScale ? canvasManager.getObjScale() : 1;

  const handleAddPreset = (preset) => {
    if (!canvasManager) return;
    const text = customText.trim() || preset.preview;
    canvasManager.addText(text, {
      fontSize: Math.round(preset.fontSize * scale),
      fontWeight: preset.fontWeight,
      fill: '#000000',
    });
    setCustomText('');
    onClose();
  };

  const handleAddCustom = () => {
    if (!canvasManager || !customText.trim()) return;
    canvasManager.addText(customText.trim(), {
      fontSize: Math.round(24 * scale),
      fontWeight: 'normal',
      fill: '#000000',
    });
    setCustomText('');
    onClose();
  };

  const handleAdd3D = (presetId) => {
    if (!canvasManager) return;
    const text = customText.trim() || 'Dein Text';
    canvasManager.add3DText(presetId, text, 72);
    setCustomText('');
    onClose();
  };

  const CURVE_EFFECTS = [
    { id: 'arc-top', label: 'Bogen oben', icon: '⌒' },
    { id: 'arc-bottom', label: 'Bogen unten', icon: '⌣' },
    { id: 'circle', label: 'Kreis', icon: '◯' },
    { id: 'wave', label: 'Welle', icon: '∿' },
    { id: 'skew-right', label: 'Neigung →', icon: '⟋' },
    { id: 'skew-left', label: 'Neigung ←', icon: '⟍' },
  ];

  const handleCurveEffect = (effectId) => {
    if (!canvasManager) return;
    const obj = canvasManager.canvas?.getActiveObject();
    if (obj && ['i-text', 'text', 'textbox', 'extrusion3DIText', 'outlineStackIText', 'neonIText', 'retroIText'].includes(obj.type)) {
      canvasManager.applyTextEffect(effectId);
      onClose();
    } else {
      // Kein Text ausgewählt → neuen Text mit Effekt erstellen
      const text = customText.trim() || 'Dein Text';
      canvasManager.addText(text, { fontSize: Math.round(48 * scale), fontWeight: 'bold', fill: '#000000' });
      setTimeout(() => canvasManager.applyTextEffect(effectId), 100);
      setCustomText('');
      onClose();
    }
  };

  const [suggestCat, setSuggestCat] = useState(null);
  const productName = new URLSearchParams(window.location.search).get('product') || '';

  const handleSuggestionSelect = (suggestion) => {
    setCustomText(suggestion);
  };

  const tabs = [
    { id: 'vorschlaege', label: 'Vorschlaege' },
    { id: 'standard', label: 'Standard' },
    { id: '3d', label: '3D Effekte' },
    { id: 'curve', label: 'Biegen' },
  ];

  return (
    <MobileBottomSheet isOpen={isOpen} onClose={onClose} title="Text" height="60vh">
      {/* Custom text input */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            ref={inputRef}
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder="Text eingeben..."
            spellCheck={true}
            lang="de"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustom(); }}
            style={{
              flex: 1, padding: '10px 12px', border: '1px solid var(--selenny-border)',
              borderRadius: '8px', fontSize: '16px', background: 'var(--selenny-surface)',
              color: 'var(--selenny-text)', outline: 'none', minWidth: 0,
            }}
          />
          <button
            onClick={handleAddCustom}
            disabled={!customText.trim()}
            style={{
              padding: '10px 16px', background: 'var(--selenny-primary)', color: '#fff',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 500,
              cursor: 'pointer', opacity: customText.trim() ? 1 : 0.5, flexShrink: 0,
            }}
          >
            Hinzufügen
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', background: 'var(--selenny-bg)', borderRadius: '8px', padding: '3px' }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1, padding: '7px', border: 'none', borderRadius: '6px', fontSize: '13px',
              fontWeight: tab === t.id ? 600 : 400, cursor: 'pointer',
              background: tab === t.id ? 'var(--selenny-surface)' : 'transparent',
              color: tab === t.id ? 'var(--selenny-text)' : 'var(--selenny-text)',
              boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Textvorschlaege */}
      {tab === 'vorschlaege' && (() => {
        const categories = Object.keys(CATEGORY_LABELS);
        const { category: detectedCat, ageGroup } = detectContext(customText, productName);
        const currentCat = suggestCat || detectedCat;

        const getManualSuggestions = (cat) => {
          const keywordMap = {
            geburtstag: 'geburtstag', hochzeit: 'hochzeit', jubilaeum: 'jubilaeum',
            firmen_event: 'firma', baby: 'baby', abschied: 'abschied',
            schulanfang: 'schulanfang', silvester: 'silvester', vatertag: 'vatertag', muttertag: 'muttertag',
          };
          return getSuggestions(keywordMap[cat] || cat, '');
        };

        const shownSuggestions = suggestCat ? getManualSuggestions(suggestCat) : getSuggestions(customText, productName);

        return (
          <div>
            {/* Kategorie-Chips */}
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', marginBottom: '10px', paddingBottom: '4px', scrollbarWidth: 'none' }}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSuggestCat(suggestCat === cat ? null : cat)}
                  style={{
                    flexShrink: 0, padding: '6px 10px', borderRadius: '20px', border: 'none',
                    fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                    background: (suggestCat || detectedCat) === cat ? 'var(--selenny-primary, #2563eb)' : 'var(--selenny-bg)',
                    color: (suggestCat || detectedCat) === cat ? '#fff' : 'var(--selenny-text)',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {CATEGORY_ICONS[cat]} {CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
            {ageGroup && !suggestCat && (
              <div style={{ fontSize: '11px', color: 'var(--selenny-primary, #2563eb)', fontWeight: 600, marginBottom: '8px' }}>
                Erkannt: {ageGroup}. Geburtstag
              </div>
            )}
            {/* Vorschlag-Pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
              {shownSuggestions.length === 0 && (
                <p style={{ fontSize: '12px', opacity: 0.5, color: 'var(--selenny-text)' }}>Keine Vorschlaege fuer diese Kategorie.</p>
              )}
              {shownSuggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  style={{
                    padding: '7px 12px', fontSize: '12px', borderRadius: '20px',
                    border: '1px solid var(--selenny-border)',
                    background: customText === suggestion ? 'var(--selenny-primary, #2563eb)' : 'var(--selenny-surface)',
                    color: customText === suggestion ? '#fff' : 'var(--selenny-text)',
                    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Standard text presets */}
      {tab === 'standard' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {TEXT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handleAddPreset(preset)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px', border: '1px solid var(--selenny-border)', borderRadius: '10px',
                background: 'var(--selenny-surface)', cursor: 'pointer', textAlign: 'left',
                WebkitTapHighlightColor: 'transparent', color: 'var(--selenny-text)',
              }}
            >
              <span style={{ fontSize: `${Math.min(preset.fontSize, 24)}px`, fontWeight: preset.fontWeight }}>
                {preset.label}
              </span>
              <span style={{ fontSize: '11px', opacity: 0.4 }}>{preset.desc}</span>
            </button>
          ))}
        </div>
      )}

      {/* 3D Effect presets */}
      {tab === '3d' && (
        <div>
          {EFFECT_CATEGORIES.map((cat) => (
            <div key={cat} style={{ marginBottom: '14px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px', color: 'var(--selenny-text)' }}>
                {cat}
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                {TEXT_3D_PRESETS.filter(p => p.category === cat).map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => handleAdd3D(preset.id)}
                    style={{
                      padding: '14px 6px', borderRadius: '10px', border: '1px solid var(--selenny-border)',
                      cursor: 'pointer', textAlign: 'center', WebkitTapHighlightColor: 'transparent',
                      background: preset.preview.bg, color: preset.preview.color,
                      fontSize: '11px', fontWeight: 700, lineHeight: 1.2,
                    }}
                  >
                    <span style={{ display: 'block', fontSize: '16px', marginBottom: '4px' }}>Aa</span>
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Curve/Bend effects */}
      {tab === 'curve' && (
        <div>
          <p style={{ fontSize: '12px', opacity: 0.5, marginBottom: '10px', color: 'var(--selenny-text)' }}>
            Wähle zuerst einen Text aus, dann tippe auf einen Effekt:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {CURVE_EFFECTS.map((effect) => (
              <button
                key={effect.id}
                onClick={() => handleCurveEffect(effect.id)}
                style={{
                  padding: '16px 8px', borderRadius: '12px',
                  border: '1px solid var(--selenny-border)',
                  background: 'var(--selenny-surface)',
                  cursor: 'pointer', textAlign: 'center',
                  color: 'var(--selenny-text)',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <span style={{ display: 'block', fontSize: '22px', marginBottom: '4px' }}>{effect.icon}</span>
                <span style={{ fontSize: '11px', fontWeight: 500 }}>{effect.label}</span>
              </button>
            ))}
            <button
              onClick={() => handleCurveEffect('none')}
              style={{
                padding: '16px 8px', borderRadius: '12px',
                border: '1px solid var(--selenny-border)',
                background: 'var(--selenny-surface)',
                cursor: 'pointer', textAlign: 'center',
                color: '#ef4444',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <span style={{ display: 'block', fontSize: '22px', marginBottom: '4px' }}>✕</span>
              <span style={{ fontSize: '11px', fontWeight: 500 }}>Zurücksetzen</span>
            </button>
          </div>
        </div>
      )}
    </MobileBottomSheet>
  );
}
